import { useState, useEffect } from 'react'
import { resultKey } from './search'
import { bookNames } from './bibles'

export const useLocalStorage = (key, initialValue, restore = value => value, store = value => value) => {
  const getValue = () => {
    try {
      return restore(JSON.parse(localStorage.getItem(key)))
    }
    catch {
      return initialValue
    }
  }

  const [value, setValue] = useState(() => getValue() ?? initialValue)

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(store(value)))
    }
    catch {}
  }, [value])

  return [value, setValue]
}

export const useEventListener = (element, event, listener, dependencies) => {
  return useEffect(() => {
    element?.addEventListener(event, listener)

    return () => element?.removeEventListener(event, listener)
  }, dependencies)
}

// Klawiatura na iOS nie zmienia `dvh` i przesuwa widoczny obszar, więc gdy jest otwarta, jego położenie
// i wysokość wystawiamy jako zmienne CSS (pasek zostaje u góry, lista kończy się nad klawiaturą).
// Z `lock` blokujemy przewijanie strony poza elementami z `data-scrollable` (iOS ignoruje przy dotyku
// `overflow: hidden`), a bez niego przewinięcie zdejmuje fokus z pola i chowa klawiaturę.
export const useViewport = (active, lock) => {
  useEffect(() => {
    const viewport = window.visualViewport
    const root = document.documentElement

    if (!active) return

    const onResize = () => {
      root.style.setProperty('--viewport-top', `${viewport.offsetTop}px`)
      root.style.setProperty('--viewport-height', `${viewport.height}px`)
    }

    const onTouchMove = e => lock
      ? e.target.closest?.('[data-scrollable]') || e.preventDefault()
      : document.activeElement.contains(e.target) || document.activeElement.blur()

    viewport && onResize()
    viewport?.addEventListener('resize', onResize)
    viewport?.addEventListener('scroll', onResize)
    document.addEventListener('touchmove', onTouchMove, {passive: !lock})

    return () => {
      viewport?.removeEventListener('resize', onResize)
      viewport?.removeEventListener('scroll', onResize)
      document.removeEventListener('touchmove', onTouchMove)
      root.style.removeProperty('--viewport-top')
      root.style.removeProperty('--viewport-height')
    }
  }, [active, lock])
}

export const useActiveText = ({input, output, search, active}) => {
  let text, verses

  if (active.search) {
    text = search.results.filter(match => active.verses[resultKey(match)]).map(match => (
      `${match.text} (${bookNames(search.bible)[match.book]} ${match.chapter}:${match.verse})`
    ))

    return text.join(' ') || null
  }
  else {
    text = Object.entries(output).filter(([id]) => active.verses[id]).map(([, match]) => match)
    verses = Object.keys(output).filter(id => active.verses[id]).map((i, id, arr) => {
      if ((id == arr.length - 1) && (arr[id + 1] - i != 1) && (i - arr[id - 1] != 1)) return i
      else if ((id == arr.length - 1) && (arr[id + 1] - i != 1) && (i - arr[id - 1] == 1)) return `-${i}`
      else if ((id != arr.length - 1) && (arr[id + 1] - i != 1) && (i - arr[id - 1] != 1)) return `${i},`
      else if ((id != arr.length - 1) && (arr[id + 1] - i != 1) && (i - arr[id - 1] == 1)) return `-${i},`
      else if ((arr[id + 1] - i == 1) && (i - arr[id - 1] != 1)) return i
      else if ((arr[id + 1] - i == 1) && (i - arr[id - 1] == 1)) return ''
    })

    if (!Object.values(active.verses).includes(true)) {
      return null
    }
    else if (!Object.values(active.verses).includes(false) && Object.keys(active.verses).length == Object.keys(output).length) {
      return `${text.join(' ')} (${bookNames(input.bible)[input.book]} ${input.chapter})`
    }
    else {
      return `${text.join(' ')} (${bookNames(input.bible)[input.book]} ${input.chapter}:${verses.join('')})`
    }
  }
}
